import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertTriangle, Phone, MessageSquare, ExternalLink } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface CrisisResource {
  country_code: string;
  country_name: string;
  emergency_number: string;
  suicide_hotline?: string;
  suicide_hotline_name?: string;
  crisis_text?: string;
  crisis_chat_url?: string;
  additional_resources?: Array<{name: string; phone?: string; text?: string}>;
}

interface CrisisButtonProps {
  user: any;
}

export const CrisisButton = ({ user }: CrisisButtonProps) => {
  const [selectedCountry, setSelectedCountry] = useState<string>('GB');
  const [crisisResources, setCrisisResources] = useState<CrisisResource[]>([]);
  const [currentResource, setCurrentResource] = useState<CrisisResource | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadCrisisResources();
  }, []);

  useEffect(() => {
    const resource = crisisResources.find(r => r.country_code === selectedCountry);
    setCurrentResource(resource || null);
  }, [selectedCountry, crisisResources]);

  const loadCrisisResources = async () => {
    try {
      const { data, error } = await supabase
        .from('international_crisis_resources')
        .select('*')
        .order('country_name');

      if (error) throw error;
      setCrisisResources((data || []) as unknown as CrisisResource[]);
    } catch (error) {
      console.error('Error loading crisis resources:', error);
      toast.error('Failed to load crisis resources');
    } finally {
      setLoading(false);
    }
  };

  const handleCall = (number: string, type: string) => {
    // Log the crisis event
    logCrisisEvent(type, number);
    
    // Attempt to initiate call
    window.location.href = `tel:${number}`;
  };

  const logCrisisEvent = async (actionTaken: string, details?: string) => {
    try {
      await supabase
        .from('crisis_events')
        .insert({
          user_id: user?.id,
          action_taken: actionTaken as any,
          notes: details,
          severity_score: 8 // High severity for crisis button usage
        });
    } catch (error) {
      console.error('Error logging crisis event:', error);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center p-8">Loading crisis resources...</div>;
  }

  return (
    <div className="space-y-6">
      <Card className="border-primary/20 bg-gradient-therapeutic">
        <CardHeader>
          <CardTitle className="text-primary flex items-center gap-2">
            <AlertTriangle className="h-5 w-5" />
            Crisis Resources - I Need Help Now
          </CardTitle>
          <p className="text-foreground">
            If you're in immediate danger, call emergency services first.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Select your country:</label>
            <Select value={selectedCountry} onValueChange={setSelectedCountry}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Choose your country" />
              </SelectTrigger>
              <SelectContent>
                {crisisResources.map((resource) => (
                  <SelectItem key={resource.country_code} value={resource.country_code}>
                    {resource.country_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {currentResource && (
            <div className="space-y-4">
              {/* Emergency Services */}
              <Card className="border-primary/30">
                <CardContent className="p-4">
                  <h3 className="font-semibold text-primary mb-2">🚨 Emergency Services</h3>
                  <Button
                    onClick={() => handleCall(currentResource.emergency_number, 'emergency_services')}
                    className="w-full bg-primary hover:bg-primary/90 text-primary-foreground text-lg py-3"
                  >
                    <Phone className="h-5 w-5 mr-2" />
                    Call {currentResource.emergency_number}
                  </Button>
                </CardContent>
              </Card>

              {/* Suicide Hotline */}
              {currentResource.suicide_hotline && (
                <Card className="border-secondary/30">
                  <CardContent className="p-4">
                    <h3 className="font-semibold text-foreground mb-2">
                      📞 {currentResource.suicide_hotline_name || 'Crisis Hotline'}
                    </h3>
                    <Button
                      onClick={() => handleCall(currentResource.suicide_hotline!, 'called_hotline')}
                      className="w-full bg-secondary hover:bg-secondary/90 text-secondary-foreground text-lg py-3 mb-2"
                    >
                      <Phone className="h-5 w-5 mr-2" />
                      Call {currentResource.suicide_hotline}
                    </Button>
                    <p className="text-sm text-foreground">24/7 confidential support</p>
                  </CardContent>
                </Card>
              )}

              {/* Crisis Text */}
              {currentResource.crisis_text && (
                <Card className="border-soft-accent/30">
                  <CardContent className="p-4">
                    <h3 className="font-semibold text-soft-accent mb-2">💬 Crisis Text Support</h3>
                    <p className="text-foreground mb-2">{currentResource.crisis_text}</p>
                    <Button
                      variant="outline"
                      className="border-soft-accent hover:bg-soft-accent/10"
                      onClick={() => {
                        logCrisisEvent('used_text_support', currentResource.crisis_text);
                        
                        // Extract the text number or keyword from the instruction text
                        const text = currentResource.crisis_text!;
                        
                        // Look for patterns like "Text SHOUT to 85258" or "Text HOME to 741741"
                        const numberMatch = text.match(/(\d{4,6})/); // 4-6 digit numbers
                        const keywordMatch = text.match(/text\s+(\w+)\s+to/i); // Extract keywords like "SHOUT" or "HOME"
                        
                        if (numberMatch) {
                          const textNumber = numberMatch[1];
                          const keyword = keywordMatch ? keywordMatch[1] : '';
                          
                          // Try to open messaging app
                          try {
                            if (keyword) {
                              // Pre-populate with the keyword if available
                              window.location.href = `sms:${textNumber}?body=${encodeURIComponent(keyword)}`;
                            } else {
                              window.location.href = `sms:${textNumber}`;
                            }
                          } catch {
                            // Fallback - show instructions
                            toast.success(`Instructions: ${text}`);
                            navigator.clipboard?.writeText(text);
                          }
                        } else {
                          // No number found, just show the instructions
                          toast.success(`Crisis text instructions: ${text}`);
                          navigator.clipboard?.writeText(text);
                        }
                      }}
                    >
                      <MessageSquare className="h-4 w-4 mr-2" />
                      Use Text Support
                    </Button>
                  </CardContent>
                </Card>
              )}

              {/* Crisis Chat */}
              {currentResource.crisis_chat_url && (
                <Card className="border-primary/30">
                  <CardContent className="p-4">
                    <h3 className="font-semibold text-primary mb-2">💻 Online Crisis Chat</h3>
                    <Button
                      variant="outline"
                      className="w-full border-primary hover:bg-primary/10"
                      onClick={() => {
                        logCrisisEvent('used_crisis_chat', currentResource.crisis_chat_url);
                        
                        try {
                          // Map old URLs to current working ones
                          let url = currentResource.crisis_chat_url!;
                          
                          // Update known broken URLs
                          if (url.includes('suicidepreventionlifeline.org')) {
                            url = 'https://988lifeline.org/chat/';
                          } else if (url.includes('samaritans.org/how-we-can-help/contact-samaritan/')) {
                            // Samaritans online chat is currently unavailable - direct to main contact page
                            url = 'https://www.samaritans.org/how-we-can-help/contact-samaritan/';
                            toast.error('Online chat currently unavailable. Please call 116 123 instead.');
                            return;
                          }
                          
                          // Ensure URL has proper protocol
                          if (!url.startsWith('http://') && !url.startsWith('https://')) {
                            url = 'https://' + url;
                          }
                          
                          // Open in new window with better handling
                          const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
                          
                          // Check if popup was blocked
                          setTimeout(() => {
                            if (!newWindow || newWindow.closed || typeof newWindow.closed == 'undefined') {
                              window.location.href = url;
                            }
                          }, 100);
                          
                        } catch {
                          toast.error('Unable to open chat link. Please copy this URL: ' + currentResource.crisis_chat_url);
                          navigator.clipboard?.writeText(currentResource.crisis_chat_url!);
                        }
                      }}
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Open Crisis Chat
                    </Button>
                    <p className="text-xs text-muted-foreground mt-2">
                      Link: {currentResource.crisis_chat_url}
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Additional Resources */}
              {currentResource.additional_resources && currentResource.additional_resources.length > 0 && (
                <Card className="border-secondary/30">
                  <CardContent className="p-4">
                    <h3 className="font-semibold text-foreground mb-3">🆘 Additional Support</h3>
                    <div className="space-y-2">
                      {currentResource.additional_resources.map((resource, index) => (
                        <div key={index} className="flex justify-between items-center p-2 bg-secondary/10 rounded">
                          <span className="text-foreground font-medium">{resource.name}</span>
                          {resource.phone && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleCall(resource.phone!, 'called_contact')}
                              className="border-secondary hover:bg-secondary/10"
                            >
                              <Phone className="h-3 w-3 mr-1" />
                              {resource.phone}
                            </Button>
                          )}
                          {resource.text && (
                            <span className="text-sm text-muted-foreground">Text: {resource.text}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};